// Renders a footer app block (the Shopify Forms newsletter) only when needed: when the visitor reaches
// a section marked with data-load-footer-apps (rich-text setting), or when the footer gets close.
// Rendered on page load, the form pulls in hCaptcha (~1 MB) on every page view.
class LazyAppBlock extends HTMLElement {
  connectedCallback() {
    const onIntersect = (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) this.render();
    };
    this.observers = [new IntersectionObserver(onIntersect, { rootMargin: '600px 0px' })];
    this.observers[0].observe(this);

    const triggers = document.querySelectorAll('[data-load-footer-apps]');
    if (triggers.length) {
      const triggerObserver = new IntersectionObserver(onIntersect);
      triggers.forEach((trigger) => triggerObserver.observe(trigger));
      this.observers.push(triggerObserver);
    }
  }

  disconnectedCallback() {
    this.observers?.forEach((observer) => observer.disconnect());
  }

  render() {
    this.observers.forEach((observer) => observer.disconnect());
    const template = this.querySelector('template');
    if (!template) return;

    const content = template.content.cloneNode(true);
    // Scripts cloned from a template do not run; recreate them so they execute.
    content.querySelectorAll('script').forEach((original) => {
      const script = document.createElement('script');
      [...original.attributes].forEach((attribute) => script.setAttribute(attribute.name, attribute.value));
      script.textContent = original.textContent;
      original.replaceWith(script);
    });
    template.replaceWith(content);

    if (this.querySelector('[data-forms-id]')) this.renderShopifyForms();
  }

  // Shopify Forms looks for its containers only once, when its module first runs. If the container
  // came in before that, the first run fills it; otherwise run a fresh copy of the module for it.
  renderShopifyForms() {
    const loader = document.querySelector('script[src*="shopify-forms-loader"]');
    if (!loader) return;

    const existing = new Set(document.querySelectorAll('shopify-forms-embed'));
    const tidy = () => {
      document.querySelectorAll('shopify-forms-embed').forEach((embed) => {
        if (this.contains(embed)) return;
        if (!existing.has(embed)) embed.remove();
      });
      // Both runs may render into the container; keep the first form.
      this.querySelectorAll('[data-forms-id]').forEach((root) => {
        root.querySelectorAll(':scope > shopify-forms-embed:not(:first-of-type)').forEach((extra) => extra.remove());
      });
      this.querySelectorAll('shopify-forms-embed').forEach(LazyAppBlock.fixFormWidth);
    };
    const observer = new MutationObserver(tidy);
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), 30000);
    tidy();

    const runModule = () => {
      if (this.querySelector('shopify-forms-embed')) return;
      const moduleUrl = loader.src.replace(/shopify-forms-loader[^/]*\.js.*$/, 'index.js');
      import(`${moduleUrl}?lazy=${Date.now()}`).catch(() => {});
    };
    // window.formStyles is set by the module once it has run; before that its first run will find the container.
    if (window.formStyles) runModule();
    else setTimeout(runModule, 5000);
  }

  // The inline form's inner container is shifted 2px inside a clipping parent, which cuts off the
  // right border of every field. The form lives in an open shadow root, so patch it there.
  static fixFormWidth(embed) {
    const root = embed.shadowRoot;
    if (!root || embed.dataset.widthFixed) return;
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(
      'section[class*="_formContainer_"] { box-sizing: border-box; width: auto !important; margin-left: 0 !important; margin-right: 0 !important; }'
    );
    root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
    embed.dataset.widthFixed = 'true';
  }
}

if (!customElements.get('lazy-app-block')) customElements.define('lazy-app-block', LazyAppBlock);
